import React, { useState, useRef, useEffect } from 'react';
import { Plug, Zap, Code, Plus, ChevronRight, ChevronLeft, Send, CheckCircle, AlertCircle, X } from 'lucide-react';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3001';

/** Exported for hero / other surfaces that need the same catalog */
export const integrations = [

  {
    name: "Shopify",
    title: "Shopify",
    subtitle: "Integrate your e-commerce store and product data",
    image: "/integrations/shopify.webp",
    alt: "Shopify Integration",
    isCustom: false
  },
 
  {
    name: "Square",
    title: "Square",
    subtitle: "Sync payments, point of sale, and commerce data",
    image: "/integrations/square.png",
    alt: "Square Integration",
    isCustom: false
  },
  {
    name: "Meta",
    title: "Meta Ads",
    subtitle: "Connect your Facebook and Instagram ad campaigns",
    image: "/integrations/instagram.webp",
    alt: "Meta Integration",
    isCustom: false
  },
  {
    name: "Google Ads",
    title: "Google Ads",
    subtitle: "Sync your Google advertising data and campaigns",
    image: "/integrations/google_ads.png",
    alt: "Google Ads Integration",
    isCustom: false
  },
  {
    name: "Boulevard",
    title: "Boulevard",
    subtitle: "Connect your salon and spa management system",
    image: "/integrations/boulevard.png",
    alt: "Boulevard Integration",
    isCustom: false
  },
  {
    name: "Custom",
    title: "Custom Integration",
    subtitle: "Need a specific integration? We'll build it on demand",
    isCustom: true
  }
];

export const integrationsWithLogos = integrations.filter((i) => !i.isCustom && i.image);

const IntegrationsSection = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const integrationsRef = useRef(null);
  const [customModalOpen, setCustomModalOpen] = useState(false);
  const [customTitle, setCustomTitle] = useState('');
  const [customDescription, setCustomDescription] = useState('');
  const [customEmail, setCustomEmail] = useState('');
  const [customStatus, setCustomStatus] = useState(null);
  const [customMessage, setCustomMessage] = useState('');
  const [customSubmitting, setCustomSubmitting] = useState(false);

  const openCustomModal = () => setCustomModalOpen(true);
  const closeCustomModal = () => {
    setCustomModalOpen(false);
    setCustomStatus(null);
    setCustomMessage('');
    setCustomTitle('');
    setCustomDescription('');
    setCustomEmail('');
  };

  const handleCustomSubmit = async (e) => {
    e.preventDefault();
    setCustomStatus(null);
    setCustomSubmitting(true);
    try {
      const res = await fetch(`${API_BASE}/api/kolanut-landing/requests`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: customTitle.trim(),
          description: customDescription.trim(),
          email: customEmail.trim() || undefined,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setCustomStatus('error');
        setCustomMessage(data.message || data.error || 'Something went wrong.');
        return;
      }
      setCustomStatus('success');
      setCustomMessage('Thanks! We’ll look into your request.');
      setCustomTitle('');
      setCustomDescription('');
      setCustomEmail('');
      setTimeout(closeCustomModal, 1500);
    } catch (err) {
      setCustomStatus('error');
      setCustomMessage(err.message || 'Could not reach the server.');
    } finally {
      setCustomSubmitting(false);
    }
  };

  const handleScroll = () => {
    if (integrationsRef.current) {
      const container = integrationsRef.current;
      const cardElements = Array.from(container.querySelectorAll('.integration-card'));
      let closestIndex = 0;
      let closestDistance = Infinity;

      cardElements.forEach((element, index) => {
        const rect = element.getBoundingClientRect();
        const containerRect = container.getBoundingClientRect();
        const distance = Math.abs((rect.left + rect.width / 2) - (containerRect.left + containerRect.width / 2));
        if (distance < closestDistance) {
          closestDistance = distance;
          closestIndex = index;
        }
      });

      if (closestIndex !== activeIndex) {
        setActiveIndex(closestIndex);
      }
    }
  };

  useEffect(() => {
    const container = integrationsRef.current;
    let scrollTimer;
    const handleScrollDebounced = () => {
      clearTimeout(scrollTimer);
      scrollTimer = setTimeout(handleScroll, 100);
    };
    if (container) {
      container.addEventListener('scroll', handleScrollDebounced);
      return () => {
        container.removeEventListener('scroll', handleScrollDebounced);
        clearTimeout(scrollTimer);
      };
    }
  }, [activeIndex]);

  const scrollToIntegration = (index) => {
    setActiveIndex(index);
    if (integrationsRef.current) {
      const cardElements = integrationsRef.current.querySelectorAll('.integration-card');
      if (cardElements && cardElements[index]) {
        integrationsRef.current.scrollTo({
          left: cardElements[index].offsetLeft - 16,
          behavior: 'smooth'
        });
      }
    }
  };

  const handlePrev = () => {
    scrollToIntegration(Math.max(0, activeIndex - 1));
  };

  const handleNext = () => {
    scrollToIntegration(Math.min(integrations.length - 1, activeIndex + 1));
  };

  return (
    <section id="integrations" className="py-12 md:py-20 bg-white">
      <div className="max-w-[1600px] mx-auto px-0 md:px-6">
        <div className="text-center mb-8 md:mb-12 px-4 max-w-2xl mx-auto">
          <p className="text-xs sm:text-sm font-semibold uppercase tracking-[0.2em] text-black/80 mb-6">
            Integrations
          </p>
          <h2 className="text-2xl sm:text-3xl md:text-[2.125rem] font-semibold text-black tracking-tight leading-snug mb-4">
            Works with the tools you already use
          </h2>
          <p className="text-base sm:text-lg text-gray-600 leading-relaxed">
            Power your business with integrations that work together
          </p>
        </div>

        {/* Mobile: 2 cards per row, logo + title only */}
        <div className="md:hidden grid grid-cols-2 gap-3 px-4">
          {integrations.map((integration, index) => (
            <div key={index} className="integration-card">
              {integration.isCustom ? (
                <button
                  type="button"
                  onClick={openCustomModal}
                  className="w-full bg-gradient-to-br from-[#c4ffa0] to-green-500 border border-[#c4ffa0] rounded-xl shadow-md p-4 flex flex-col items-center justify-center min-h-[100px] hover:shadow-lg transition-shadow text-left"
                >
                  <div className="flex items-center justify-center h-10 w-10 mb-2">
                    <Code className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-sm font-bold text-white text-center leading-tight">
                    Custom
                  </h3>
                </button>
              ) : (
                <div className="bg-white border border-gray-200/50 rounded-xl shadow-md p-4 flex flex-col items-center justify-center min-h-[100px]">
                  <div className="flex items-center justify-center h-10 mb-2">
                    <img
                      src={integration.image}
                      alt={integration.alt}
                      className="w-full h-auto max-h-10 object-contain"
                    />
                  </div>
                  <h3 className="text-sm font-bold text-gray-800 text-center leading-tight">
                    {integration.title}
                  </h3>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Desktop navigation controls */}
        <div className="hidden md:flex justify-end mb-4 gap-2 px-4">
          <button
            onClick={handlePrev}
            disabled={activeIndex === 0}
            className={`p-2 rounded-full border ${
              activeIndex === 0
                ? 'border-gray-200 text-gray-300 cursor-not-allowed'
                : 'border-gray-200 text-gray-600 hover:bg-gray-100'
            }`}
            aria-label="Previous integration"
          >
            <ChevronLeft size={20} />
          </button>
          <button
            onClick={handleNext}
            disabled={activeIndex === integrations.length - 1}
            className={`p-2 rounded-full border ${
              activeIndex === integrations.length - 1
                ? 'border-gray-200 text-gray-300 cursor-not-allowed'
                : 'border-gray-200 text-gray-600 hover:bg-gray-100'
            }`}
            aria-label="Next integration"
          >
            <ChevronRight size={20} />
          </button>
        </div>

        {/* Desktop: horizontal scroll */}
        <div className="hidden md:block w-full overflow-hidden">
          <div
            ref={integrationsRef}
            className="flex overflow-x-auto hide-scrollbar snap-x snap-mandatory pb-6 px-0"
          >
            {integrations.map((integration, index) => (
              <div
                key={index}
                className={`integration-card snap-center w-[calc(40%-32px)] lg:w-[calc(25%-32px)] mr-4 flex-shrink-0 transition-all duration-500 h-[280px] ${
                  activeIndex === index ? 'scale-100 opacity-100' : 'scale-95 opacity-85'
                }`}
              >
                {integration.isCustom ? (
                  <button
                    type="button"
                    onClick={openCustomModal}
                    className="w-full h-full bg-gradient-to-br from-[#c4ffa0] to-green-500 border border-[#c4ffa0] rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 p-6 lg:p-8 flex flex-col items-center justify-center min-h-[260px] group hover:scale-[1.02] text-left"
                  >
                    <div className="mb-4 flex items-center justify-center h-20">
                      <div className="bg-white/20 backdrop-blur-sm rounded-lg p-4 shadow-lg">
                        <Code className="w-8 h-8 lg:w-10 lg:h-10 text-white" />
                      </div>
                    </div>
                    <h3 className="text-lg lg:text-xl font-bold text-white mb-2 text-center">
                      Custom Integration
                    </h3>
                    <p className="text-sm lg:text-base text-white/90 text-center">
                      Need a specific integration? We'll build it on demand
                    </p>
                    <div className="mt-4 flex items-center text-white/80 text-xs lg:text-sm">
                      <Plus className="w-4 h-4 mr-1" />
                      <span>Available on request</span>
                    </div>
                  </button>
                ) : (
                  <div className="bg-white border border-gray-200/50 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 p-6 lg:p-8 flex flex-col items-center justify-center h-full min-h-[260px] group hover:scale-[1.02] hover:border-[#c4ffa0]">
                    <div className="mb-4 flex items-center justify-center h-20">
                      <img
                        src={integration.image}
                        alt={integration.alt}
                        className="w-full h-auto max-h-16 lg:max-h-20 object-contain transition-all duration-300"
                      />
                    </div>
                    <h3 className="text-lg lg:text-xl font-bold text-gray-800 mb-2 text-center">
                      {integration.title}
                    </h3>
                    <p className="text-sm lg:text-base text-gray-600 text-center">
                      {integration.subtitle}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Custom integration request modal */}
      {customModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={closeCustomModal}>
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <h3 className="text-lg font-bold text-gray-800">Request a custom integration</h3>
              <button type="button" onClick={closeCustomModal} className="p-1 rounded-lg hover:bg-gray-100" aria-label="Close">
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            <form onSubmit={handleCustomSubmit} className="p-4 space-y-4">
              <div>
                <label htmlFor="custom-title" className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
                <input
                  id="custom-title"
                  type="text"
                  required
                  value={customTitle}
                  onChange={(e) => setCustomTitle(e.target.value)}
                  placeholder="e.g. Klaviyo integration"
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#c4ffa0] focus:border-black outline-none"
                />
              </div>
              <div>
                <label htmlFor="custom-description" className="block text-sm font-medium text-gray-700 mb-1">Description *</label>
                <textarea
                  id="custom-description"
                  required
                  rows={3}
                  value={customDescription}
                  onChange={(e) => setCustomDescription(e.target.value)}
                  placeholder="What do you need?"
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#c4ffa0] focus:border-black outline-none resize-y"
                />
              </div>
              <div>
                <label htmlFor="custom-email" className="block text-sm font-medium text-gray-700 mb-1">Email <span className="text-red-500">*</span></label>
                <input
                  id="custom-email"
                  type="email"
                  required
                  value={customEmail}
                  onChange={(e) => setCustomEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#c4ffa0] focus:border-black outline-none"
                />
              </div>
              {customStatus && (
                <div className={`flex items-center gap-2 px-3 py-2 rounded-xl text-sm ${customStatus === 'success' ? 'bg-green-50 text-green-800 border border-green-200' : 'bg-red-50 text-red-800 border border-red-200'}`}>
                  {customStatus === 'success' ? <CheckCircle className="w-4 h-4 flex-shrink-0" /> : <AlertCircle className="w-4 h-4 flex-shrink-0" />}
                  <span>{customMessage}</span>
                </div>
              )}
              <div className="flex gap-2 pt-1">
                <button type="button" onClick={closeCustomModal} className="flex-1 px-4 py-2.5 border border-gray-300 rounded-xl font-medium text-gray-700 hover:bg-gray-50">
                  Cancel
                </button>
                <button type="submit" disabled={customSubmitting} className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-black text-white font-semibold rounded-xl hover:bg-black/90 disabled:opacity-60">
                  {customSubmitting ? 'Sending…' : <><Send className="w-4 h-4" /> Send request</>}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <style jsx>{`
        .hide-scrollbar {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        .hide-scrollbar::-webkit-scrollbar {
          display: none;
        }
      `}</style>
    </section>
  );
};

export default IntegrationsSection;
